import { FC } from 'react'

import { SettingsPageLayout } from '../components/SettingsPageLayout'
import {
  IconWorld,
  IndigoIndigo60,
  Link,
  SettingsFormControl,
  SettingsFormControlIcon,
  SettingsGroup,
  Switch,
  TextInputField
} from '@sequencehq/core-components'

import { useQuery } from '@sequencehq/api'

import {
  BASE_INPUT_FIELD_STYLES,
  SETTINGS_STALE_TIME
} from '../settings.constants'
import { useEndCustomerPortalSettings } from 'SettingsV2/components/portal/useEndCustomerPortalSettings'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'

type CustomerPortalAuthRequirement =
  DashboardApi20240730.GetMerchantForSequenceAccount.Merchant['customerPortalAuthRequirement']

export const EndCustomerPortalSettingsPage: FC = () => {
  const { data, isError, isLoading } = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount,
    undefined,
    {
      staleTime: SETTINGS_STALE_TIME
    }
  )

  const { isPending, queries, fields, mutate } =
    useEndCustomerPortalSettings(data)

  return (
    <SettingsPageLayout header="End-customer portal" isError={isError}>
      <SettingsGroup isLoading={isLoading}>
        {data && (
          <SettingsFormControl
            label="Enable end-customer portal"
            helper="Hosted portal for customers to view invoices, usage and credit balances."
            icon={
              <SettingsFormControlIcon>
                <IconWorld />
              </SettingsFormControlIcon>
            }
            htmlFor="enable-customer-portal"
          >
            <Switch
              size="md"
              id="enable-customer-portal"
              data-testid="settings.portal.enablePortalSwitch"
              disabled={isPending}
              isChecked={fields.customerPortalEnabled.value}
              onChange={value => {
                fields.customerPortalEnabled.onChange(value)
                mutate<boolean>('customerPortalEnabled', {
                  label: 'End-customer portal',
                  value
                })
              }}
            />
          </SettingsFormControl>
        )}
      </SettingsGroup>
      {data?.customerPortalEnabled && (
        <>
          <SettingsGroup>
            <SettingsFormControl
              label="Show usage in end-customer portal"
              htmlFor="show-usage"
            >
              <Switch
                size="md"
                id="show-usage"
                disabled={isPending}
                isChecked={fields.showUsageInCustomerPortal.value}
                onChange={value => {
                  fields.showUsageInCustomerPortal.onChange(value)
                  mutate<boolean>('showUsageInCustomerPortal', {
                    label: 'Usage in customer portal',
                    value
                  })
                }}
              />
            </SettingsFormControl>

            <SettingsFormControl
              label="Display a link back to your application"
              htmlFor="show-app-url"
            >
              <Switch
                size="md"
                id="show-app-url"
                disabled={isPending}
                isChecked={fields.showAppUrl.value}
                onChange={value => {
                  fields.showAppUrl.onChange(value)
                  if (!value) {
                    fields.appUrl.onChange(undefined)
                    mutate<undefined>('appUrl', {
                      label: 'App URL',
                      value: undefined
                    })
                  }
                }}
              />
            </SettingsFormControl>

            {queries.formData.showAppUrl && (
              <SettingsFormControl
                label="Your application URL"
                htmlFor="app-url"
              >
                <TextInputField
                  {...BASE_INPUT_FIELD_STYLES}
                  autoFocus
                  id="app-url"
                  placeholder="https://"
                  disabled={isPending}
                  value={fields.appUrl.value}
                  validationErrors={fields.appUrl.validationErrors}
                  onChange={e => {
                    fields.appUrl.onChange(e.target.value)
                  }}
                  onBlur={() => {
                    mutate<string | undefined>('appUrl', {
                      label: 'App URL',
                      value: fields.appUrl.value
                    })
                  }}
                />
              </SettingsFormControl>
            )}
          </SettingsGroup>

          <SettingsGroup
            title="Authentication"
            footer={
              <SettingsGroup.Footer>
                When enabled, end users must verify their identity before
                accessing the customer portal.{' '}
                <Link
                  to="https://docs.sequencehq.com/customers/customer-portal#portal-authentication"
                  isExternal
                  color={IndigoIndigo60}
                >
                  Learn more
                </Link>
              </SettingsGroup.Footer>
            }
          >
            <SettingsFormControl
              label="Require authentication to access the portal"
              htmlFor="customer-portal-auth-requirement"
            >
              <Switch
                size="md"
                id="customer-portal-auth-requirement"
                disabled={isPending}
                isChecked={fields.customerPortalAuthRequirement.value}
                onChange={value => {
                  fields.customerPortalAuthRequirement.onChange(value)
                  mutate<CustomerPortalAuthRequirement>(
                    'customerPortalAuthRequirement',
                    {
                      label: 'Portal authentication',
                      value: value ? 'REQUIRED' : 'NOT_REQUIRED'
                    }
                  )
                }}
              />
            </SettingsFormControl>
          </SettingsGroup>
        </>
      )}
    </SettingsPageLayout>
  )
}
